import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, EyeOff, ExternalLink, Globe, Loader2, RotateCcw, Sparkles, UserX } from "lucide-react";
import { DEMO_SLUG, type SiteDemo } from "@wedding/shared";
import { adminDemoApi, adminSiteApi, type DemoStatus } from "../lib/api";
import { AppLink } from "../lib/route-primitives";
import { paths } from "../lib/paths";
import { cn } from "../lib/utils";

/**
 * "Satış & Site → Misafir demosu" (F7): platformun örnek düğünü `/${DEMO_SLUG}` (`sample-wedding`).
 * Bu bir DAVETİYE örneği değil — misafir uygulamasının (yükleme/galeri/Sahne)
 * telefonsuz denemesi. Davetiye örneği landing'de ayrı (`SAMPLE_INVITE_PATH`).
 *
 * Kur/Sıfırla: düğünü (yoksa) açar, her şeyi silip tohumlar — vitrin
 * karelerinden (yoksa stok görsellerden) onaylı kareler + örnek yorumlar.
 * Ziyaretçiler telefonsuz girer, yüklemeleri moderasyona düşer ve gece silinir.
 * Sitede görünmesi ayrıca `site:demo.show` ile (yayınla / yayından kaldır);
 * demo kurulmadan yayınlanamaz, yayından kaldırma her zaman mümkün.
 */

const WEB_BASE =
  (import.meta.env.VITE_WEB_BASE_URL as string | undefined)?.replace(/\/$/, "") ?? "http://localhost:5173";

export function DemoTab({ demo, onDemoChange, showToast }: {
  demo: SiteDemo;
  onDemoChange: (d: SiteDemo) => void;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<DemoStatus | null>(null);
  const [busy, setBusy] = useState<"setup" | "clear" | "cta" | null>(null);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      const res = await adminDemoApi.status();
      setStatus(res.data ?? null);
    } catch {
      setLoadError(true);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const fail = (err: unknown) =>
    showToast(t("siteAdmin.saveFailed", { error: err instanceof Error ? err.message : String(err) }), false);

  const setup = async () => {
    if (status?.exists && !window.confirm(t("siteAdmin.demo.resetConfirm"))) return;
    setBusy("setup");
    try {
      const res = await adminDemoApi.setup();
      if (res.data) setStatus(res.data.status);
      showToast(t("siteAdmin.demo.setupDone", { count: res.data?.seeded ?? 0 }));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg === "slug_taken") showToast(t("siteAdmin.demo.slugTaken", { slug: DEMO_SLUG }), false);
      else fail(err);
    } finally {
      setBusy(null);
    }
  };

  const clearVisitors = async () => {
    if (!window.confirm(t("siteAdmin.demo.clearConfirm"))) return;
    setBusy("clear");
    try {
      const res = await adminDemoApi.clearVisitors();
      if (res.data) setStatus(res.data.status);
      showToast(t("siteAdmin.demo.cleared", { count: res.data?.guests ?? 0 }));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  const setPublished = async (show: boolean) => {
    setBusy("cta");
    try {
      const res = await adminSiteApi.put("demo", { show });
      onDemoChange(res.data ?? { show });
      showToast(t(show ? "siteAdmin.demo.published" : "siteAdmin.demo.unpublished"));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  const ready = status?.isDemo === true;
  const btn =
    "inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed";

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="px-5 py-4 border-b border-border bg-muted/30">
          <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
            {t("siteAdmin.demo.title")}
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">{t("siteAdmin.demo.desc")}</p>
        </div>

        <div className="px-5 py-4 space-y-4">
          {loadError ? (
            <p className="text-sm text-red-500">{t("siteAdmin.loadFailed")}</p>
          ) : !status ? (
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          ) : status.slugTaken ? (
            <p className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-600 dark:text-red-400" data-testid="demo-slug-taken">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              {t("siteAdmin.demo.slugTaken", { slug: DEMO_SLUG })}
            </p>
          ) : (
            <dl className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="demo-status">
              {[
                { k: "siteAdmin.demo.state", v: status.exists ? t("siteAdmin.demo.live") : t("siteAdmin.demo.none") },
                { k: "siteAdmin.demo.seedMedia", v: status.seedMedia },
                { k: "siteAdmin.demo.visitors", v: status.visitors },
                { k: "siteAdmin.demo.pending", v: status.pendingMedia },
              ].map((row) => (
                <div key={row.k} className="rounded-xl border border-border px-3 py-2.5">
                  <dt className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{t(row.k)}</dt>
                  <dd className="mt-1 text-lg font-semibold tabular-nums text-foreground">{row.v}</dd>
                </div>
              ))}
            </dl>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void setup()}
              disabled={busy !== null || !status || status.slugTaken}
              className={cn(btn, "bg-primary text-primary-foreground hover:bg-primary/90")}
            >
              {busy === "setup" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
              {status?.exists ? t("siteAdmin.demo.reset") : t("siteAdmin.demo.setup")}
            </button>
            <button
              type="button"
              onClick={() => void clearVisitors()}
              disabled={busy !== null || !ready}
              className={cn(btn, "border border-border hover:bg-primary/5")}
            >
              {busy === "clear" ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserX className="h-4 w-4" />}
              {t("siteAdmin.demo.clearVisitors")}
            </button>
            {ready && (
              <>
                <a
                  href={`${WEB_BASE}/${DEMO_SLUG}/auth`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cn(btn, "border border-border hover:bg-primary/5")}
                >
                  <ExternalLink className="h-4 w-4" />
                  {t("siteAdmin.demo.open")}
                </a>
                <AppLink to={paths.wedding.moderation(DEMO_SLUG)} className={cn(btn, "border border-border hover:bg-primary/5")}>
                  {t("siteAdmin.demo.moderation")}
                </AppLink>
              </>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{t("siteAdmin.demo.seedHint")}</p>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card px-5 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4" data-testid="demo-publish">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground flex flex-wrap items-center gap-2">
            <span>{t("siteAdmin.demo.ctaTitle")}</span>
            <span
              data-testid="demo-publish-state"
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold",
                demo.show
                  ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                  : "bg-muted text-muted-foreground",
              )}
            >
              <span className={cn("h-1.5 w-1.5 rounded-full", demo.show ? "bg-emerald-500" : "bg-muted-foreground/60")} aria-hidden="true" />
              <span>{demo.show ? t("siteAdmin.demo.onSite") : t("siteAdmin.demo.offSite")}</span>
            </span>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{t("siteAdmin.demo.ctaDesc")}</p>
        </div>
        {demo.show ? (
          <button
            type="button"
            onClick={() => void setPublished(false)}
            disabled={busy !== null}
            className={cn(btn, "shrink-0 border border-border hover:bg-red-500/5 hover:text-red-600")}
          >
            {busy === "cta" ? <Loader2 className="h-4 w-4 animate-spin" /> : <EyeOff className="h-4 w-4" />}
            <span>{t("siteAdmin.demo.unpublish")}</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void setPublished(true)}
            disabled={busy !== null || !ready}
            className={cn(btn, "shrink-0 bg-primary text-primary-foreground hover:bg-primary/90")}
          >
            {busy === "cta" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
            <span>{t("siteAdmin.demo.publish")}</span>
          </button>
        )}
      </div>
    </div>
  );
}
